import React, { useState, useEffect , lazy, Suspense } from 'react';
import CourseList from '../components/course/CourseList';
import FoundationCourseList from '../components/course/FoundationCourseList';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { themeConfig } from '../styles/colors';
import { courseSectionTranslations } from '../utils/gradeTranslations';
import teacherImage from '../assets/images/teachers/omar-elkholy.png';
import HomePopup from './homepop-up';
import CodeEntryForm from '../components/common/CodeEntryForm';
import Footer from '../components/common/Footer';
import { BookOpen } from 'lucide-react';
const GradeCards = lazy(() => import('../components/common').then(module => ({ default: module.GradeCards })));


// Define styles outside of the component
const globalStyles = `
  .kadence-theme {
    --primary: ${themeConfig.primary};
    --primary-light: ${themeConfig.light};
    --secondary: ${themeConfig.secondary};
    --dark: ${themeConfig.dark};
    --light: ${themeConfig.light};
    font-family: 'Cairo', 'Tajawal', 'Poppins', 'Segoe UI', sans-serif;
    direction: rtl;
  }
  
  .font-tajawal {
    font-family: 'Tajawal', sans-serif;
  }
  
  .font-cairo {
    font-family: 'Cairo', sans-serif;
  }
  
  .animate-blob {
    animation: blob 7s infinite;
  }
  
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
  
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
    100% { transform: translateY(0px); }
  }
  
  .animate-subtle-pulse {
    animation: subtle-pulse 4s ease-in-out infinite;
  }
  
  @keyframes subtle-pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.02); }
    100% { transform: scale(1); }
  }
  
  .animate-fade-in-down {
    animation: fade-in-down 0.5s ease-out forwards;
  }
  
  @keyframes fade-in-down {
    0% { opacity: 0; transform: translateY(-10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  .animate-slide-up {
    animation: slide-up 0.5s ease-out forwards;
  }
  
  @keyframes slide-up {
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  .animate-slide-right {
    animation: slide-right 0.5s ease-out forwards;
  }
  
  @keyframes slide-right {
    0% { opacity: 0; transform: translateX(-20px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  
  .animate-scale-in {
    animation: scale-in 0.5s ease-out forwards;
  }
  
  @keyframes scale-in {
    0% { opacity: 0; transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
`;
// Optimized floating icons - reduced count and simplified animations
const createOptimizedIcons = (section: string = 'default') => {
  const icons = [
    // Reduced from 21 to 8 icons for better performance
    { src: 'anubis', top: '5%', left: '5%', size: '35px', delay: 0 },
    { src: 'hieroglyph', top: '8%', left: '25%', size: '40px', delay: 0.5 },
    { src: 'pharaoh', top: '12%', left: '45%', size: '45px', delay: 1 },
    { src: 'globe', top: '6%', left: '65%', size: '38px', delay: 1.5 },
    { src: 'world', top: '15%', left: '85%', size: '60px', delay: 2 },
    { src: 'anubis', top: '45%', left: '12%', size: '34px', delay: 2.5 },
    { src: 'hieroglyph', top: '65%', left: '52%', size: '41px', delay: 3 },
    { src: 'pharaoh', top: '85%', left: '38%', size: '44px', delay: 3.5 },
  ];

  return icons.map((icon, index) => (
    <div
      key={`${section}-icon-${index}`}
      className="absolute pointer-events-none z-10 opacity-15"
      style={{
        top: icon.top,
        left: icon.left,
        width: icon.size,
        height: icon.size,
        animationDelay: `${icon.delay}s`,
        animationDuration: '4s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-in-out',
        willChange: 'transform'
      }}
    >
      <div className="w-full h-full bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full animate-pulse"></div>
    </div>
  ));
};

// Memoized section component
const PremiumSection = React.memo(({ 
  children, 
  title, 
  badgeText, 
  icon: Icon,
  id
 }: { 
  children: React.ReactNode;
  title: string;
  badgeText: string;
  icon: React.ComponentType<any>;
  id?: string;
}) => (
     <section id={id} className="py-6 sm:py-12 lg:py-16 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden">
    {/* Simplified decorative borders */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
    
    {/* Simplified background elements */}
    <div className="absolute inset-0">
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-purple-400/10 to-blue-400/10 rounded-full blur-2xl"></div>
    </div>
    
    {/* Floating icons */}
    <div className="absolute inset-0 pointer-events-none z-10">
      {createOptimizedIcons(title.toLowerCase())}
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      {/* Merged header and content container */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Header section at the top */}
                 

        {/* Content section below header */}
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="relative z-10">
            {children}
          </div>
        </div>
      </div>
    </div>
  </section>
));

PremiumSection.displayName = 'PremiumSection';


const Home: React.FC = () => {
  const { isAuthenticated, currentUser } = useAuth();
  const { colors, componentColors } = useTheme();
  const [showPopup, setShowPopup] = useState(false);
  
  // Show popup when component mounts
  useEffect(() => {
    setShowPopup(true);
  }, []);
  
  const scrollToCourses = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  return (
    <React.Fragment>
      <style>{globalStyles}</style>
      <HomePopup isOpen={showPopup} onClose={handleClosePopup} />
      
      {/* Floating Sticky Button - Only show for non-authenticated users */}
      {!isAuthenticated && (
        <Link
          to="/register"
          className="fixed bottom-6 left-6 z-40 bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 font-tajawal text-sm flex items-center gap-2 group"
          style={{
            animation: 'bounce 2s infinite',
          }}
        >
          <svg className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>تسجيل جديد</span>
          
          {/* Pulse effect */}
          <div className="absolute inset-0 bg-teal-400 rounded-full animate-ping opacity-20"></div>
        </Link>
      )}
      
      <div className={`kadence-theme min-h-screen ${colors.background.primary}`} dir="rtl">
        {/* Hero Section */}
        <div className={`hero-section relative ${componentColors.hero.wrapper} overflow-hidden pt-20`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-28 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Hero content */}
              <div className="text-right lg:text-right order-2 lg:order-1">
                <div className={`inline-flex items-center px-4 py-2 ${componentColors.hero.badge} rounded-full mb-6 animate-fade-in-down`}>
                  <span className={colors.text.accent}>Practice Makes Perfect</span>
                  <span className={`w-2 h-2 rounded-full ${colors.accent.teal.medium} mr-2 animate-pulse`}></span>
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight">
                  <span className={`block animate-slide-up opacity-0 ${componentColors.hero.heading}`} style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>!   It's just English    </span>
                  <span className={`block ${componentColors.hero.subheading} animate-slide-up opacity-0`} style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>No fear.. Success is near!</span>
                </h1>
                <div className={`mt-6 animate-slide-up opacity-0 bg-gradient-to-r from-slate-800/5 to-transparent p-6 rounded-2xl border-r-4 border-teal-500`} style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
                  <p className={`text-2xl font-bold ${componentColors.hero.description} mb-3 font-tajawal text-right`}>
                    بتعاني مع الإنجليزي وتايه بين الكلمات والجرامر والقصة وال skills؟
                  </p>
                  <p className={`text-lg ${componentColors.hero.description} max-w-2xl font-cairo text-right`}>
                    مستر عمر وفريقه معاك في مشوارك. في منصة متكاملة بتجهزك للامتحان النهائي؛ 
                    <span className="text-teal-600 font-semibold mx-1">كلمات / جرامر / قصة / مهارات</span> 
                    <span className="text-teal-600 font-semibold mx-1">/ واجبات / حل / متابعة.</span>
                     كل دا على <span className="font-bold" style={{ color: 'rgb(13 148 136 / var(--tw-text-opacity, 1))' }}>منصة واحدة</span>! وبأسلوب شرح وتدرج يناسب كل المستويات!
                  </p>
                </div>
                <div className="mt-10 flex flex-col sm:flex-row justify-end lg:justify-end space-y-4 sm:space-y-0 sm:space-x-4 rtl:space-x-reverse animate-fade-in opacity-0" style={{ animationDelay: '1.2s', animationFillMode: 'forwards' }}>
                  {isAuthenticated ? (
                    <>
                      <Link to="/enrolled-courses" className={componentColors.primaryButton}>
                        My Courses
                      </Link>
                      <button onClick={scrollToCourses} className={componentColors.secondaryButton}>
                        Browse More Courses
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={scrollToCourses} className={componentColors.primaryButton}>
                        Start Learning
                      </button>
                      <Link to="/about" className={componentColors.secondaryButton}>
                        Meet Mr. Omar
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {/* Teacher Profile */}
              <div className="mt-10 lg:mt-0 order-1 lg:order-2 animate-float">
                <div className="relative mx-auto max-w-md">
                  <div className={`kadence-blob absolute -top-8 -left-8 w-full h-full ${colors.accent.blob.teal} rounded-full filter blur-3xl opacity-70 animate-blob`}></div>
                  <div className={`kadence-blob absolute -bottom-8 -right-8 w-full h-full ${colors.accent.blob.purple} rounded-full filter blur-3xl opacity-70 animate-blob animation-delay-2000`}></div>
                  <div className={`kadence-blob absolute -bottom-8 left-20 w-full h-full ${colors.accent.blob.pink} rounded-full filter blur-3xl opacity-70 animate-blob animation-delay-4000`}></div>
                  <div className={`relative rounded-2xl overflow-hidden shadow-2xl ${colors.border.white} border-4 transform hover:scale-[1.02] transition-all duration-500 animate-scale-in opacity-0`} style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
                    <img
                      className="w-full h-auto object-cover animate-subtle-pulse"
                      src={teacherImage}
                      alt="Mr. Omar Elkholy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <div className="flex items-center mb-2 animate-slide-right opacity-0" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
                        <div className={`w-10 h-1 ${colors.accent.teal.medium} rounded-full mr-3`}></div>
                        <h3 className="text-2xl font-bold">Mr. Omar Elkholy</h3>
                      </div>
                      <p className={colors.text.light + " animate-slide-right opacity-0"} style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
                        
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* About Section */}
        
        {/* Features Section */}
        <div className={`py-24 ${colors.background.white} relative overflow-hidden`} dir="rtl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-2 font-tajawal text-center">
                <span className="text-gray-800">ليه </span> <span className="text-teal-500">تنضم لنا </span>
              </h2>
              <div className="relative w-64 h-8 mx-auto">
                <svg className="absolute top-2 right-0 w-full" viewBox="0 0 200 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M 0,10 C 50,-10 150,30 200,10" fill="none" stroke="#0BC5EA" strokeWidth="3" />
                </svg>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <div className="bg-sky-100 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <svg className="w-10 h-10 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 font-tajawal">هندربك</h3>
                <p className="text-gray-600 font-cairo">
                  امتحانات بعد كل حصة وامتحانات شاملة كتير
                </p>
              </div>
              
              {/* Feature 2 */}
              <div className="bg-sky-100 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <svg className="w-10 h-10 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 font-tajawal">هنجهزك</h3>
                <p className="text-gray-600 font-cairo">
                  مش محتاج تسأل اذاكر أيه,  احنا مخططين لك كل حاجة
                </p>
              </div>
              
              {/* Feature 3 */}
              <div className="bg-sky-100 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <svg className="w-10 h-10 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 font-tajawal">هتنافس</h3>
                <p className="text-gray-600 font-cairo">
                 هتلاقي مجتمع كامل بيذاكر معاك في المنصة
                </p>
              </div>
              
              {/* Feature 4 */}
              <div className="bg-sky-100 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <svg className="w-10 h-10 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 font-tajawal">هنتشارك</h3>
                <p className="text-gray-600 font-cairo">
                  مجموعات للمناقشة عشان تسأل وتتشارك أفكارك مع زمايلك
                </p>
              </div>
            </div>
          </div>
          
{/* YouTube Player Section */}
<div className={`py-16 sm:py-20 md:py-24 ${colors.background.white} relative`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12 sm:mb-14 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 font-tajawal text-center leading-tight">
                <span className="text-gray-800">ايه هو كورس التأسيس </span> 
                <span className="text-teal-500 block sm:inline">English Basics ؟</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-cairo px-4">
              
              </p>
            </div>
            
            <div className="max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto px-2 sm:px-4">
              <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl md:shadow-2xl">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/51xSlw0BZdo?rel=0&modestbranding=1&playsinline=0"
                  title="مقدمة منصة أ. عمر الخولي التعليمية"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; screen-wake-lock"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
          
          {/* Background decoration */}
          <div className="absolute -top-6 sm:-top-8 md:-top-10 -left-6 sm:-left-8 md:-left-10 w-24 sm:w-32 md:w-40 h-24 sm:h-32 md:h-40 bg-teal-100 rounded-full opacity-50"></div>
          <div className="absolute -bottom-6 sm:-bottom-8 md:-bottom-10 -right-6 sm:-right-8 md:-right-10 w-24 sm:w-32 md:w-40 h-24 sm:h-32 md:h-40 bg-indigo-100 rounded-full opacity-50"></div>
        </div>
        </div>
        
        {/* Foundation Courses Section */}
        <div className={`py-24 ${colors.background.white} relative`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className={`text-3xl font-bold ${componentColors.sectionHeader.title} sm:text-4xl font-tajawal text-center`} dir="rtl">
                <span className="relative inline-block">
                  <span className={`absolute inset-x-0 bottom-0 h-3 ${componentColors.sectionHeader.highlight}`}></span>
                  <span className="relative">كورسات التأسيس</span>
                </span>
              </h2>
              <p className={`mt-4 text-xl ${componentColors.sectionHeader.description} max-w-3xl mx-auto font-cairo text-center`} dir="rtl">
                كورسات تأسيسية لجميع المراحل الدراسية
              </p>
            </div>
            <div className="kadence-course-list">
              <FoundationCourseList />
            </div>
          </div>
        </div>
        
      {/* Grade Selection Section */}
      <PremiumSection id="courses-section" title="" badgeText="" icon={BookOpen}>
        <Suspense fallback={<div className="text-center py-6">جاري التحميل...</div>}>
           <GradeCards />
        </Suspense>
      </PremiumSection>

        {/* Footer */}
        
      </div>
    </React.Fragment>
  );
};

export default Home; 